/**
 * Access-review task — V98-3-5 (ADR-0046 T14–T15; research §2 rule 13).
 * Per-row routing: the reviewer never reviews their own access, the
 * approver never approves a row they reviewed or are the subject of, and
 * reciprocal pairs route to a third holder. Rows needing judgement come
 * first (exceptions), routine rows after (the mobile quick path). Each row
 * shows only the action THIS viewer may take; the rest is state.
 *
 * R3-S5 (server) + its UI Blocker: rows nobody eligible can take are listed
 * together ("Nobody eligible · N rows", each with the server's plain-language
 * reason), with the actions THIS caller has: add an independent party for
 * this review (owner/admin, or the review's program lead — POST
 * /coverage/independent-parties with `for`) or ask the program lead / an
 * admin to add one (POST /coverage/independent-parties/ask). When neither is
 * open the server's `why` says who can — never a silent state.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input } from '@aigovops/design-system';
import type { BlockingRow } from '@aigovops/core';
import {
  accessReviewRows, addIndependentParty, approveAccessRow, askForIndependentParty, completeAccessReview, decideAccessRow, type PartyActions,
} from '../../lib/coverage-api';
import { groupRows, type RowView } from '../../lib/item-model';
import { firstName, nameOf, type Names } from '../../lib/today-model';
import { Chip, domId, errorText } from '../today/shared';

export function AccessReviewTask({ slug, reviewId, me, names, isReviewer, onAddParty, onDone, system, onClearSystem }: {
  slug: string; reviewId: string; me: string; names: Names; isReviewer: boolean;
  onAddParty: (opener: HTMLElement) => void; onDone: (msg: string, focusId: string) => void;
  /** V98-5 review T5-H12: the system the rows are filtered to (from the progress panel's "N to review"). */
  system?: string | null; onClearSystem?: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['access-review-rows', slug, reviewId], queryFn: () => accessReviewRows(slug, reviewId), retry: false });
  const [error, setError] = useState<string | null>(null);
  if (q.isLoading) return <p className="gp-dim">Loading rows…</p>;
  if (q.error || !q.data) return <Alert variant="error">The rows could not load.</Alert>;
  const shown = system ? q.data.rows.filter((r) => r.system_key === system) : q.data.rows;
  const { exceptions, routine } = groupRows(shown, me, names);

  const act = async (v: RowView, fn: () => Promise<unknown>, msg: string) => {
    setError(null);
    try { await fn(); await qc.invalidateQueries({ queryKey: ['access-review-rows', slug, reviewId] }); await qc.invalidateQueries({ queryKey: ['today', slug] }); onDone(msg, domId('row', v.row.item_key)); }
    catch (e) { setError(errorText(e, 'That did not go through.')); }
  };
  const subject = (v: RowView) => (v.row.subject_user_id ? nameOf(names, v.row.subject_user_id) : v.row.subject_state === 'no_person' ? 'Service identity' : 'Unknown owner');
  const rowEl = (v: RowView) => (
    <li key={v.row.item_key} className="gp-ow" id={domId('row', v.row.item_key)} tabIndex={-1} data-row={v.row.item_key}>
      <span className="gp-ow-main">
        <span className="gp-ow-line"><span className="gp-ow-t">{v.row.item_key}</span><span className="gp-dim">{subject(v)}</span></span>
        {/* U13: one chip for the state, the rest as plain meta text. */}
        <span className="gp-ow-meta">
          <Chip text={v.label} tone={v.row.approval ? 'ok' : 'neutral'} />
          <span>{v.meta}</span>
        </span>
      </span>
      <span className="gp-ow-act">
        {v.action === 'review' ? (
          <>
            <Button compact aria-label={`Keep · ${v.row.item_key}`} onClick={() => void act(v, () => decideAccessRow(slug, reviewId, { item_key: v.row.item_key, decision: 'keep' }), `Kept ${v.row.item_key}`)}>Keep</Button>
            <Button compact aria-label={`Revoke · ${v.row.item_key}`} onClick={() => void act(v, () => decideAccessRow(slug, reviewId, { item_key: v.row.item_key, decision: 'revoke' }), `Revoke recorded for ${v.row.item_key}`)}>Revoke</Button>
          </>
        ) : v.action === 'approve' ? (
          <Button compact aria-label={`Approve · ${v.row.item_key}`} onClick={() => void act(v, () => approveAccessRow(slug, reviewId, v.row.item_key), `Approved ${v.row.item_key}`)}>Approve</Button>
        ) : v.action === 'add_party' ? (
          <Button compact id={domId('addparty', v.row.item_key)} aria-label={`Add an independent party · ${v.row.item_key}`} onClick={(e) => onAddParty(e.currentTarget)}>Add an independent party</Button>
        ) : null}
      </span>
    </li>
  );
  const complete = async () => {
    setError(null);
    try { await completeAccessReview(slug, reviewId); await qc.invalidateQueries({ queryKey: ['today', slug] }); onDone('Access review completed', 'review-state'); }
    catch (e) { setError(errorText(e, 'The review can’t complete yet.')); }
  };
  return (
    <div className="gp-form">
      {q.data.blocking?.length ? (
        <BlockingRows slug={slug} reviewId={reviewId} rows={q.data.blocking} actions={q.data.actions ?? null} names={names} onDone={onDone} />
      ) : null}
      <p className="gp-sec-count" id="review-state" tabIndex={-1}>{`Approved `}<b>{q.data.approved}</b>{` of `}<b>{q.data.total}</b>{` rows`}</p>
      {system ? (
        <p className="gp-ow-meta" data-part="rows-filter" id="rows-filter" tabIndex={-1}>
          <span>{`Showing ${system} · ${shown.length} of ${q.data.total} rows`}</span>
          {onClearSystem ? <Button compact variant="ghost" onClick={onClearSystem}>Show every system</Button> : null}
        </p>
      ) : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      {exceptions.length ? (
        <section aria-labelledby="rows-exc-h" className="gp-sec">
          <h3 id="rows-exc-h" className="gp-sec-h">{`Needs attention · ${exceptions.length}`}</h3>
          <ul className="gp-owed-list">{exceptions.map(rowEl)}</ul>
        </section>
      ) : null}
      {routine.length ? (
        <section aria-labelledby="rows-rt-h" className="gp-sec">
          <h3 id="rows-rt-h" className="gp-sec-h">{`Routine · ${routine.length}`}</h3>
          <ul className="gp-owed-list">{routine.map(rowEl)}</ul>
        </section>
      ) : null}
      {isReviewer ? (
        <div className="gp-actions">
          {q.data.all_approved
            ? <Button variant="primary" onClick={() => void complete()}>Complete review</Button>
            : <span className="gp-auto"><LockGlyph />Closes when every system is attested and every row approved</span>}
        </div>
      ) : null}
    </div>
  );
}

function LockGlyph() {
  return <svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" /></svg>;
}

/** R3-S5: the rows nobody eligible can take, and what this caller can do about them. */
function BlockingRows({ slug, reviewId, rows, actions, names, onDone }: {
  slug: string; reviewId: string; rows: readonly BlockingRow[]; actions: PartyActions | null; names: Names;
  onDone: (msg: string, focusId: string) => void;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [asked, setAsked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const add = actions?.add_party;
  const ask = actions?.ask_lead;
  const lead = ask?.to_user_id ? nameOf(names, ask.to_user_id) : 'the program lead';
  const subject = (r: BlockingRow) => (r.subject_user_id ? nameOf(names, r.subject_user_id) : 'Service identity');
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['access-review-rows', slug, reviewId] }), qc.invalidateQueries({ queryKey: ['today', slug] })]);
  const askNow = async () => {
    setError(null);
    try {
      const r = await askForIndependentParty(slug, reviewId);
      const to = nameOf(names, r.to_user_id, lead);
      setAsked(`Asked ${firstName(to)}`);
      await refresh();
      onDone(`Asked ${to} to add an independent party`, 'blocking-actions');
    } catch (e) { setError(errorText(e, 'The ask could not be sent.')); }
  };
  return (
    <section className="gp-state gp-state--warn" aria-labelledby="blocking-h" data-part="blocking-rows">
      <h3 id="blocking-h" className="gp-sec-h">{`Nobody eligible · ${rows.length} row${rows.length === 1 ? '' : 's'}`}</h3>
      <ul className="gp-plain-list" aria-label="Rows nobody eligible can take">
        {rows.map((r) => (
          <li key={`${r.item_key}:${r.step}`} className="gp-ow-line">
            <span className="gp-ow-t">{r.item_key}</span>
            <span className="gp-dim">{`${subject(r)} · ${r.step === 'review' ? 'review' : 'approval'} · ${r.reason_text[r.reason_text.length - 1] ?? (r.status === 'needs_independent_party' ? 'needs an independent party' : 'nobody eligible')}`}</span>
          </li>
        ))}
      </ul>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div className="gp-actions" id="blocking-actions" tabIndex={-1}>
        {add?.available && !adding
          ? <Button compact id="blocking-add-party" aria-expanded={false} onClick={() => setAdding(true)}>Add an independent party</Button>
          : null}
        {ask?.available ? (asked ? <Chip text={asked} tone="lock" /> : <Button compact onClick={() => void askNow()}>{`Ask ${firstName(lead)} to add one`}</Button>) : null}
        {!add?.available && !ask?.available ? <span className="gp-dim">{add?.why || ask?.why || 'An owner or admin adds independent parties.'}</span> : null}
      </div>
      {adding && add ? (
        <PartyForm slug={slug} reviewId={reviewId} onCancel={() => { setAdding(false); window.setTimeout(() => document.getElementById('blocking-add-party')?.focus(), 0); }}
          onDone={async (m) => { setAdding(false); await refresh(); onDone(m, 'blocking-actions'); }} />
      ) : null}
    </section>
  );
}

function PartyForm({ slug, reviewId, onCancel, onDone }: { slug: string; reviewId: string; onCancel: () => void; onDone: (m: string) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!name.trim()) errs.name = 'Enter their name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Enter a work email, like name@firm.com.';
    setErrors(errs);
    if (errs.name || errs.email) return;
    setBusy(true); setError(null);
    try {
      const r = await addIndependentParty(slug, { kind: 'advisor_seat', contact_name: name.trim(), contact_email: email.trim(), for: { kind: 'access_review', id: reviewId } });
      // A lead adding someone who isn't a member yet comes back 202 { status: 'asked' }: an admin sends the invite.
      const routed = (r as unknown as { status?: string }).status === 'asked';
      onDone(routed ? `Asked an admin to invite ${name.trim()}` : r.state === 'active' ? `${name.trim()} can take these rows now` : `Invite pending · ${name.trim()} takes these rows once they accept`);
    } catch (err) { setError(errorText(err, 'The independent party could not be added.')); }
    finally { setBusy(false); }
  };
  return (
    <form className="gp-form" onSubmit={submit} noValidate aria-label="Add an independent party">
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="blocking-party-name" label="Name" required error={errors.name}>
        <Input ref={first} value={name} maxLength={200} onChange={(e) => { setName(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, name: undefined })); }} />
      </Field>
      <Field id="blocking-party-email" label="Work email" required hint="Outside these rows' reporting lines · never your audit firm" error={errors.email}>
        <Input type="email" value={email} maxLength={320} onChange={(e) => { setEmail(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, email: undefined })); }} />
      </Field>
      <div className="gp-actions">
        <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Adding…' : 'Add independent party'}</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
